/**
 * Client-side AI model registry: task ids, display names, plan → model mapping, legacy proxy
 * mapping and deterministic seed helper (CONTRACTS §1/§2).
 *
 * Mirrors `supabase/functions/_shared/gemini-models.ts` and `cloud-run-worker/geminiModels.js`.
 * The client never chooses the model when talking to gemini-proxy v2 (it sends a `task`);
 * the model ids here are for display and for the v1-proxy fallback path only.
 */

/** Current model ids (display + v1 fallback). Server env may override the real ids. */
export const MODEL_IDS = Object.freeze({
  premium: 'gemini-4-argon',
  fast: 'gemini-3.7-flash',
  lastResort: 'gemini-3-flash-preview',
  lite: 'gemini-3.5-flash-lite',
});

/**
 * Model ids the previous (v1) gemini-proxy accepts. v1 collapses any other id to flash, so the
 * v1 fallback path must send these, never the new ids.
 */
export const LEGACY_PROXY_MODEL_IDS = Object.freeze({
  tier: 'gemini-3.1-pro-preview',
  fast: 'gemini-3-flash-preview',
  lite: 'gemini-3-flash-preview',
});

/** Tiers that receive the premium model on tier-class tasks. */
export const PREMIUM_TIERS = Object.freeze(['pro', 'pro-plus', 'enterprise']);

/** Canonical task ids (same strings as the edge and worker registries). */
export const AI_TASKS = Object.freeze({
  EDITOR_INLINE: 'editor.inline',
  DOC_PATCH: 'doc.patch',
  CHAT_TITLE: 'chat.title',
  UTIL: 'util',
  CHAT_AGENT: 'chat.agent',
  DOC_PATCH_ESCALATE: 'doc.patch.escalate',
  DOC_REWRITE: 'doc.rewrite',
  DOC_GENERATE: 'doc.generate',
  REQUIREMENTS_EXTRACT: 'requirements.extract',
  SESSION_INSIGHTS: 'session.insights',
  TEMPLATE_ANALYZE: 'template.analyze',
  PRESENTATION: 'presentation',
  MEETING_AUTOPILOT: 'meeting.autopilot',
  MEETING_FOLLOWUP: 'meeting.followup',
  MEETING_CLASSIFY: 'meeting.classify',
  MEETING_FILING: 'meeting.filing',
  TEMPLATE_SUGGEST: 'template.suggest',
});

/**
 * Task → model class. 'fast' = fast model for every tier; 'tier' = premium for premium tiers;
 * 'lite' = lite model for every tier (always-on meeting steps).
 */
export const TASK_CLASS = Object.freeze({
  'editor.inline': 'fast',
  'doc.patch': 'fast',
  'chat.title': 'fast',
  util: 'fast',
  'chat.agent': 'tier',
  'doc.patch.escalate': 'tier',
  'doc.rewrite': 'tier',
  'doc.generate': 'tier',
  'requirements.extract': 'tier',
  'session.insights': 'lite',
  'template.analyze': 'tier',
  presentation: 'tier',
  'meeting.autopilot': 'lite',
  'meeting.followup': 'lite',
  'meeting.classify': 'lite',
  'meeting.filing': 'lite',
  'template.suggest': 'lite',
});

/** Every model id ever stored or sent, mapped to its class. */
export const LEGACY_MODEL_CLASS = Object.freeze({
  'gemini-2.5-pro': 'tier',
  'gemini-3.1-pro-preview': 'tier',
  'gemini-4-argon': 'tier',
  'gemini-2.5-flash': 'fast',
  'gemini-3-flash-preview': 'fast',
  'gemini-3.7-flash': 'fast',
});

/** Human-readable names for model ids. */
export const MODEL_DISPLAY_NAMES = Object.freeze({
  'gemini-4-argon': 'Gemini 4 Argon',
  'gemini-3.7-flash': 'Gemini 3.7 Flash',
  'gemini-3.5-flash-lite': 'Gemini 3.5 Flash Lite',
  'gemini-3-flash-preview': 'Gemini 3 Flash',
  'gemini-3.1-pro-preview': 'Gemini 3.1 Pro',
  'gemini-2.5-pro': 'Gemini 2.5 Pro',
  'gemini-2.5-flash': 'Gemini 2.5 Flash',
});

/**
 * Canonicalize a task id; unknown or missing ids become 'util'.
 * @param {string} [task] - Task id.
 * @returns {string} Known task id.
 */
export function normalizeTask(task) {
  return typeof task === 'string' && Object.prototype.hasOwnProperty.call(TASK_CLASS, task)
    ? task
    : AI_TASKS.UTIL;
}

/**
 * Model class for a task.
 * @param {string} [task] - Task id.
 * @returns {'tier' | 'fast' | 'lite'} Class.
 */
export function getTaskClass(task) {
  return TASK_CLASS[normalizeTask(task)];
}

/**
 * Whether a tier gets the premium model on tier-class tasks.
 * @param {string | null | undefined} tier - Tier key.
 * @returns {boolean} True for pro, pro-plus and enterprise.
 */
export function isPremiumTier(tier) {
  return typeof tier === 'string' && PREMIUM_TIERS.includes(tier);
}

/**
 * Model the server will use for a task and tier (display / logging only).
 * @param {{ task?: string, tier?: string | null }} params - Task and tier.
 * @returns {string} Model id.
 */
export function resolveModel({ task, tier } = {}) {
  const cls = getTaskClass(task);
  if (cls === 'lite') return MODEL_IDS.lite;
  return cls === 'tier' && isPremiumTier(tier) ? MODEL_IDS.premium : MODEL_IDS.fast;
}

/**
 * The plan's headline model (what heavy AI features run on).
 * @param {string | null | undefined} tier - Tier key.
 * @returns {string} Model id.
 */
export function getPlanModelId(tier) {
  return isPremiumTier(tier) ? MODEL_IDS.premium : MODEL_IDS.fast;
}

/**
 * Display name for a model id.
 * @param {string} modelId - Model id.
 * @returns {string} Display name (falls back to the id).
 */
export function getModelDisplayName(modelId) {
  return MODEL_DISPLAY_NAMES[modelId] || modelId || '';
}

/**
 * Map any stored model id (legacy or current) onto the current id of the same class.
 * Unknown ids return null so callers can pick the plan default.
 * @param {string} modelId - Stored model id.
 * @returns {string | null} Current model id or null.
 */
export function normalizeStoredModelId(modelId) {
  const cls = LEGACY_MODEL_CLASS[modelId];
  if (cls === 'tier') return MODEL_IDS.premium;
  if (cls === 'fast') return MODEL_IDS.fast;
  return null;
}

/**
 * Model id to send as `modelName` to a v1 gemini-proxy for a task.
 * @param {string} [task] - Task id.
 * @returns {string} Legacy model id.
 */
export function legacyModelNameForTask(task) {
  return LEGACY_PROXY_MODEL_IDS[getTaskClass(task)];
}

/**
 * 32-bit FNV-1a over the UTF-16 code units of a string (`charCodeAt`), offset basis
 * 0x811c9dc5, prime 0x01000193. Identical to the worker (`cloud-run-worker/stableHash.js`),
 * the edge registry and `src/utils/templateFields.js`.
 * @param {string} input - Input string.
 * @returns {number} Unsigned 32-bit hash.
 */
export function fnv1a32(input) {
  const str = String(input ?? '');
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * FNV-1a as 8 lowercase hex chars.
 * @param {string} input - Input string.
 * @returns {string} Hex hash.
 */
export function fnv1a32Hex(input) {
  return fnv1a32(input).toString(16).padStart(8, '0');
}

/**
 * Deterministic JSON (object keys sorted recursively; undefined members dropped).
 * @param {unknown} value - JSON-compatible value.
 * @returns {string} Canonical JSON text.
 */
export function canonicalJson(value) {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return `[${value.map((item) => canonicalJson(item)).join(',')}]`;
  if (typeof value === 'object') {
    const keys = Object.keys(value).filter((key) => value[key] !== undefined).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * Canonical string form of one seed part: null/undefined → '', primitives → String, objects → canonical JSON.
 * @param {unknown} part - Seed part.
 * @returns {string} Canonical string.
 */
function canonicalSeedPart(part) {
  if (part === null || part === undefined) return '';
  if (typeof part === 'string') return part;
  if (typeof part === 'number' || typeof part === 'boolean' || typeof part === 'bigint') return String(part);
  return canonicalJson(part);
}

/**
 * Stable 31-bit seed: FNV-1a of the canonical parts joined with '|', masked to 31 bits.
 * Same algorithm as the edge and worker. Increment a `variant` part for "Regenerate".
 * @param {...unknown} parts - Values to hash.
 * @returns {number} Seed in [0, 2^31).
 * @example stableSeed('doc.patch', documentId, instruction, 0)
 */
export function stableSeed(...parts) {
  return fnv1a32(parts.map(canonicalSeedPart).join('|')) & 0x7fffffff;
}

/** Generation profiles (CONTRACTS §2). Mirrors the edge `gemini-profiles.ts`. */
export const GENERATION_PROFILES = Object.freeze({
  extract: Object.freeze({ temperature: 0.2, topP: 0.8, thinkingLevel: 'low', json: true, maxOutputTokens: 16384 }),
  fill: Object.freeze({ temperature: 0.3, topP: 0.9, thinkingLevel: 'low', json: true, maxOutputTokens: 16384 }),
  patch: Object.freeze({ temperature: 0.2, topP: 0.8, thinkingLevel: 'low', json: true, maxOutputTokens: 8192 }),
  inline: Object.freeze({ temperature: 0.4, topP: 0.95, thinkingLevel: 'minimal', json: false, maxOutputTokens: 4096 }),
  chat: Object.freeze({ temperature: 0.6, topP: 0.95, thinkingLevel: 'low', json: false, maxOutputTokens: 8192 }),
  creative: Object.freeze({ temperature: 0.9, topP: 0.95, thinkingLevel: 'medium', json: false, maxOutputTokens: 8192 }),
});

/** Default profile per task. */
export const DEFAULT_TASK_PROFILE = Object.freeze({
  'editor.inline': 'inline',
  'doc.patch': 'patch',
  'doc.patch.escalate': 'patch',
  'doc.rewrite': 'fill',
  'doc.generate': 'fill',
  'chat.title': 'extract',
  'chat.agent': 'chat',
  'requirements.extract': 'extract',
  'session.insights': 'extract',
  'template.analyze': 'extract',
  presentation: 'fill',
  util: 'inline',
  'meeting.autopilot': 'extract',
  'meeting.followup': 'extract',
  'meeting.classify': 'extract',
  'meeting.filing': 'extract',
  'template.suggest': 'extract',
});

/**
 * Profile for a task when the caller names none.
 * @param {string} [task] - Task id.
 * @returns {string} Profile name.
 */
export function getDefaultProfileForTask(task) {
  return DEFAULT_TASK_PROFILE[normalizeTask(task)] || 'inline';
}

const SCHEMA_KEYS_KEPT = new Set([
  'type', 'format', 'title', 'description', 'nullable', 'enum', 'maxItems', 'minItems',
  'properties', 'required', 'minProperties', 'maxProperties', 'minLength', 'maxLength',
  'pattern', 'example', 'anyOf', 'propertyOrdering', 'default', 'items', 'minimum', 'maximum',
]);

/**
 * Convert a JSON-Schema-style object (lowercase types) to Gemini `responseSchema` form
 * (uppercase types, unsupported keywords dropped). Idempotent. Same as the edge converter.
 * @param {object} schema - JSON schema.
 * @returns {object | undefined} Gemini schema.
 */
export function toGeminiResponseSchema(schema) {
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) return undefined;
  const out = {};
  Object.entries(schema).forEach(([key, value]) => {
    if (!SCHEMA_KEYS_KEPT.has(key)) return;
    if (key === 'type') {
      if (Array.isArray(value)) {
        const types = value.filter((v) => typeof v === 'string' && v.toLowerCase() !== 'null');
        if (types.length < value.length) out.nullable = true;
        if (types[0]) out.type = String(types[0]).toUpperCase();
      } else if (typeof value === 'string') {
        out.type = value.toUpperCase();
      }
    } else if (key === 'properties' && value && typeof value === 'object') {
      const props = {};
      Object.entries(value).forEach(([name, sub]) => {
        const converted = toGeminiResponseSchema(sub);
        if (converted) props[name] = converted;
      });
      out.properties = props;
    } else if (key === 'items') {
      const converted = toGeminiResponseSchema(value);
      if (converted) out.items = converted;
    } else if (key === 'anyOf' && Array.isArray(value)) {
      out.anyOf = value.map((v) => toGeminiResponseSchema(v)).filter(Boolean);
    } else {
      out[key] = value;
    }
  });
  return out;
}

/**
 * Map a thinking level onto one the legacy model accepts (Gemini 3.x Pro: low/high only).
 * @param {string} model - Model id.
 * @param {string} level - Requested level.
 * @returns {string} Supported level.
 */
function thinkingLevelForModel(model, level) {
  if (/^gemini-3(\.\d+)?-pro/.test(model || '')) {
    if (level === 'minimal') return 'low';
    if (level === 'medium') return 'high';
  }
  return level;
}

/**
 * Build a complete generationConfig for the v1 proxy, which forwards generationConfig to
 * Gemini unchanged. Applies the profile, explicit overrides (temperature 0 honored), seed,
 * schema and per-model thinking level. Never adds topK/candidateCount/empty stopSequences.
 * @param {object} params - Inputs.
 * @param {string} params.profile - Profile name.
 * @param {string} params.model - Legacy model id the v1 proxy will use.
 * @param {object} [params.overrides] - Caller generationConfig overrides.
 * @param {number} [params.seed] - Seed.
 * @param {object} [params.responseSchema] - JSON schema for structured output.
 * @param {boolean} [params.hasTools] - Whether tools are attached.
 * @returns {object} generationConfig.
 */
export function buildLegacyGenerationConfig({ profile, model, overrides = {}, seed, responseSchema, hasTools = false }) {
  const spec = GENERATION_PROFILES[profile] || GENERATION_PROFILES.inline;
  const o = overrides && typeof overrides === 'object' ? overrides : {};
  const cfg = {
    temperature: typeof o.temperature === 'number' ? o.temperature : spec.temperature,
    topP: typeof o.topP === 'number' ? o.topP : spec.topP,
  };
  let maxOut = typeof o.maxOutputTokens === 'number' ? o.maxOutputTokens : spec.maxOutputTokens;
  const effectiveSeed = typeof seed === 'number' ? seed : o.seed;
  if (typeof effectiveSeed === 'number' && Number.isFinite(effectiveSeed)) {
    cfg.seed = Math.floor(effectiveSeed) & 0x7fffffff;
  }
  if (!hasTools) {
    const schema = responseSchema || o.responseSchema;
    if (schema) {
      cfg.responseMimeType = 'application/json';
      cfg.responseSchema = toGeminiResponseSchema(schema);
    } else if (o.responseMimeType) {
      cfg.responseMimeType = o.responseMimeType;
    }
  }
  if (Array.isArray(o.stopSequences) && o.stopSequences.some((s) => typeof s === 'string' && s)) {
    cfg.stopSequences = o.stopSequences.filter((s) => typeof s === 'string' && s);
  }
  const requestedLevel = o.thinkingConfig?.thinkingLevel || spec.thinkingLevel;
  if (/^gemini-([3-9]|\d{2,})/.test(model || '')) {
    cfg.thinkingConfig = {
      thinkingLevel: thinkingLevelForModel(model, requestedLevel),
      ...(o.thinkingConfig?.includeThoughts ? { includeThoughts: true } : {}),
    };
    maxOut = Math.max(maxOut, 2048);
  } else if (o.thinkingConfig?.includeThoughts) {
    cfg.thinkingConfig = { includeThoughts: true };
  }
  cfg.maxOutputTokens = maxOut;
  return cfg;
}
